import ImageWithFallback from "@/components/application/image-with-fallback";
import SelectProductsDialog from "@/components/application/select-products-dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { X } from "lucide-react";
import { Collection, Product } from "@/types";
import { InertiaFormProps } from "@inertiajs/react";
import { FormEvent } from "react";
import useFieldArray from "@/hooks/use-field-array";
import useDialog from "@/hooks/use-dialog";
import useTranslations from "@/hooks/use-translations";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export type CollectionFormData = {
  collection: {
    name: string;
    state: string;
    product_ids: string[];
    products: Product[];
  };
};

export default function CollectionForm({
  form,
  collection,
  onSubmit,
}: {
  form: InertiaFormProps<CollectionFormData>;
  collection?: Collection;
  onSubmit: (event: FormEvent) => void;
}) {
  const { t } = useTranslations("shared.components.collection_form");
  const { remove } = useFieldArray(form, "collection.products");

  const selectProductsDialog = useDialog();

  function onSubmitForm(event: FormEvent) {
    event.preventDefault();

    form.transform((data) => ({
      ...data,
      collection: {
        ...data.collection,
        product_ids: data.collection.products.map((product) => product.id),
      },
    }));

    onSubmit(event);
  }

  return (
    <>
      <form
        id={
          collection
            ? `edit-collection-form-${collection.id}`
            : "new-collection-form"
        }
        method="post"
        className="flex-1 flex flex-col gap-5 px-4 overflow-auto"
        onSubmit={onSubmitForm}
      >
        <Field>
          <FieldLabel htmlFor="name">{t("shared.fields.name")}</FieldLabel>
          <Input
            id="name"
            name="product[name]"
            placeholder={t("name_placeholder")}
            value={form.data.collection.name ?? ""}
            onChange={(event) =>
              form.setData("collection.name", event.target.value)
            }
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="state">{t("shared.fields.state")}</FieldLabel>
          <Select
            name="collection[state]"
            defaultValue="draft"
            value={form.data.collection.state}
            onValueChange={(value) => form.setData("collection.state", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("shared.fields.state_placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">{t("state_draft")}</SelectItem>
              <SelectItem value="published">{t("state_published")}</SelectItem>
              <SelectItem value="archived">{t("state_archived")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel>{t("products_label")}</FieldLabel>

          <Table>
            <TableBody>
              {form.data.collection.products.map((product, index) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <ImageWithFallback
                      srcset={[product.image_url]}
                      alt={product.name}
                      className="size-24 object-contain"
                    />
                  </TableCell>
                  <TableCell className="max-w-60">
                    <p className="text-sm font-medium whitespace-normal">
                      {product.name}
                    </p>
                    <p className="text-sm">{product.system_code}</p>
                  </TableCell>
                  <TableCell className="w-8">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      type="button"
                      onClick={() => remove(index)}
                    >
                      <X />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Button
            type="button"
            variant="outline"
            onClick={selectProductsDialog.open}
          >
            {t("browse_products")}
          </Button>
        </Field>
      </form>

      <SelectProductsDialog
        open={selectProductsDialog.isOpen}
        onOpenChange={selectProductsDialog.setIsOpen}
        defaultValue={(collection?.products ?? []).map((product) => product.id)}
        onSelect={({ records }) => {
          form.setData("collection.products", records);
        }}
      />
    </>
  );
}
